/**
 * My Trips IA v2 — 「我的行程」primary owner（FE-IA-V2-05）。
 * Journey workspace secondary：總覽／規劃／住宿／交通／費用／旅伴（對應既有 route）。
 * Group Trip：Discover→探索；Create→＋；Manage→contextual。不與 Journey domain 合併。
 */

export const JOURNEY_CREATE_PATH = '/journeys/new';

export const MY_TRIPS_IA_CAPABILITIES = Object.freeze({
  journeyList: 'EXISTING',
  journeyOverview: 'EXISTING',
  journeyPlanning: 'EXISTING',
  journeyStays: 'EXISTING',
  journeyTransport: 'EXISTING',
  journeyCost: 'EXISTING', // budget + expenses + split
  journeyPeople: 'EXISTING',
  journeyCreate: 'EXISTING',
  groupTripDiscovery: 'EXISTING_EXPLORE',
  groupTripCreate: 'EXISTING_GLOBAL_CREATE',
  groupTripManage: 'EXISTING_CONTEXTUAL',
});

/**
 * IA v2 canonical workspace destinations → real routes（不發明新 URL）。
 * Cost 對應既有費用三頁，不以單一 /cost 捏造。
 */
export const MY_TRIPS_IA_WORKSPACE = Object.freeze([
  { iaKey: 'overview', label: '總覽', pathSuffix: '', end: true },
  { iaKey: 'planning', label: '規劃', pathSuffix: '/planning', end: true },
  { iaKey: 'stays', label: '住宿', pathSuffix: '/stays', end: true },
  { iaKey: 'transport', label: '交通', pathSuffix: '/transport', end: true },
  {
    iaKey: 'cost',
    label: '費用',
    pathSuffix: '/budget',
    end: true,
    relatedSuffixes: ['/budget', '/expenses', '/split'],
  },
  { iaKey: 'people', label: '旅伴', pathSuffix: '/people', end: true },
]);

export const MY_TRIPS_IA_ROUTE_COMPATIBILITY = Object.freeze([
  {
    oldRoute: '/journeys',
    owner: 'myTrips',
    destination: '/journeys',
    action: 'KEEP_AS_PRIMARY',
  },
  {
    oldRoute: '/journeys/new',
    owner: 'create',
    destination: '/journeys/new',
    action: 'KEEP_VIA_GLOBAL_CREATE_AND_MY_TRIPS_SHORTCUT',
  },
  {
    oldRoute: '/journeys/:journeyId',
    owner: 'myTrips.workspace',
    destination: '/journeys/:journeyId',
    action: 'KEEP_OVERVIEW',
  },
  {
    oldRoute: '/journeys/:journeyId/planning',
    owner: 'myTrips.workspace',
    destination: '/journeys/:journeyId/planning',
    action: 'KEEP',
  },
  {
    oldRoute: '/journeys/:journeyId/stays',
    owner: 'myTrips.workspace',
    destination: '/journeys/:journeyId/stays',
    action: 'KEEP',
  },
  {
    oldRoute: '/journeys/:journeyId/transport',
    owner: 'myTrips.workspace',
    destination: '/journeys/:journeyId/transport',
    action: 'KEEP',
  },
  {
    oldRoute: '/journeys/:journeyId/budget',
    owner: 'myTrips.workspace.cost',
    destination: '/journeys/:journeyId/budget',
    action: 'KEEP_COST',
  },
  {
    oldRoute: '/journeys/:journeyId/expenses',
    owner: 'myTrips.workspace.cost',
    destination: '/journeys/:journeyId/expenses',
    action: 'KEEP_COST',
  },
  {
    oldRoute: '/journeys/:journeyId/split',
    owner: 'myTrips.workspace.cost',
    destination: '/journeys/:journeyId/split',
    action: 'KEEP_COST',
  },
  {
    oldRoute: '/journeys/:journeyId/people',
    owner: 'myTrips.workspace',
    destination: '/journeys/:journeyId/people',
    action: 'KEEP',
  },
  {
    oldRoute: '/public/group-trips',
    owner: 'explore',
    destination: '/public/group-trips',
    action: 'KEEP_DISCOVERY',
  },
  {
    oldRoute: '/group-trips/picker→/group-trips/new',
    owner: 'create',
    destination: '/group-trips/picker',
    action: 'KEEP_VIA_GLOBAL_CREATE',
  },
  {
    oldRoute: '/group-trips/:groupTripId',
    owner: 'groupTrip.contextual',
    destination: '/group-trips/:groupTripId',
    action: 'KEEP_HOST_MEMBER',
  },
  {
    oldRoute: '/public/group-trips/:groupTripId',
    owner: 'explore',
    destination: '/public/group-trips/:groupTripId',
    action: 'KEEP_PUBLIC_DETAIL',
  },
]);

export function journeyWorkspacePath(journeyId, pathSuffix = '') {
  const id = String(journeyId);
  return `/journeys/${id}${pathSuffix}`;
}

export function resolveMyTripsIaKey(pathname) {
  const path = String(pathname || '');
  const match = path.match(/^\/journeys\/[^/]+(\/.*)?$/);
  if (!match) return null;
  const suffix = match[1] || '';
  if (!suffix || suffix === '/') return 'overview';
  if (suffix === '/planning' || suffix.startsWith('/planning/')) return 'planning';
  if (suffix === '/stays' || suffix.startsWith('/stays/')) return 'stays';
  if (suffix === '/transport' || suffix.startsWith('/transport/')) return 'transport';
  if (
    suffix === '/budget' || suffix.startsWith('/budget/')
    || suffix === '/expenses' || suffix.startsWith('/expenses/')
    || suffix === '/split' || suffix.startsWith('/split/')
  ) {
    return 'cost';
  }
  if (suffix === '/people' || suffix.startsWith('/people/')) return 'people';
  return null;
}
